import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import moment from 'moment';
import { Routes, isValidDate } from '../../utils';

interface FieldErrors {
    regionError: string;
    districtError: string;
    startDateError: string;
    endDateError: string;
}

const initialErrorsState: FieldErrors = {
    regionError: '',
    districtError: '',
    startDateError: '',
    endDateError: '',
};

interface Suggestion {
    id: string | number;
    name: string;
}

const useDriverReportValidation = (): any => {
    const [inputs, setInputs] = useState({
        region: 0,
        district: 0,
        startDate: new Date(''),
        endDate: new Date(''),
    });
    const [fieldErrors, setFieldErrors] = useState(initialErrorsState);

    const validateRequiredFields = (): any => {
        let regionError = '';
        let districtError = '';
        const requiredFieldMessage = 'This field is required.';
        if (inputs.region === 0) {
            regionError = requiredFieldMessage;
        }

        if (inputs.district === 0) {
            districtError = requiredFieldMessage;
        }

        return {
            regionError,
            districtError,
        };
    };

    const handleDropdownChange = (fieldName: string, newValue: string): void => {
        setInputs(inputs => ({ ...inputs, [fieldName]: +newValue }));
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
    };

    const handleAutoCompleteChange = (inputName: string, value: Suggestion): void => {
        switch (inputName) {
            case 'region': {
                setInputs({
                    ...inputs,
                    region: +value.id,
                });
                if (value.id !== 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        regionError: '',
                    });
                }
                break;
            }
            case 'district': {
                setInputs({
                    ...inputs,
                    district: +value.id,
                });
                if (value.id !== 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        districtError: '',
                    });
                }
                break;
            }
        }
    };

    const handleDateInputChange = (inputName: string, value: Date): void => {
        switch (inputName) {
            case 'startDate': {
                const startDate = !!value ? new Date(value) : null;
                let startDateError = '';

                if (!isValidDate(startDate)) {
                    startDateError = 'Please enter a valid date.';
                } else if (startDate && inputs.endDate && startDate > inputs.endDate) {
                    startDateError = 'Start date should not be greater than end date.';
                }

                setFieldErrors({
                    ...fieldErrors,
                    startDateError,
                });
                break;
            }
            case 'endDate': {
                const endDate = !!value ? new Date(value) : null;
                let endDateError = '';

                if (!isValidDate(endDate)) {
                    endDateError = 'Please enter a valid date.';
                } else if (endDate && inputs.startDate && endDate < inputs.startDate) {
                    endDateError = 'End date should not be less than start date.';
                }

                setFieldErrors({
                    ...fieldErrors,
                    endDateError,
                });
                break;
            }
        }

        setInputs({
            ...inputs,
            [inputName]: value,
        });
    };

    const handleSubmit = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        const { regionError, districtError } = validateRequiredFields();

        setFieldErrors({
            ...fieldErrors,
        });

        if (regionError === '' && districtError === '') {
            const addJobInput = {
                regionId: +inputs.region,
                districtId: +inputs.district,
            };
        }
    };

    const resetFormState = (): void => {
        setFieldErrors(initialErrorsState);
        setInputs({
            region: 0,
            district: 0,
            startDate: new Date(''),
            endDate: new Date(''),
        });
    };

    return {
        handleSubmit,
        handleDropdownChange,
        handleInputChange,
        handleAutoCompleteChange,
        handleDateInputChange,
        inputs,
        fieldErrors,
        validateRequiredFields,
        resetFormState,
    };
};

export default useDriverReportValidation;
